import type { ProductView, VendorView } from '#contract'
import type { BasketLine } from './basket.ts'

/** A basket priced from the vendor's page, as the basket shows it. */
export interface BasketSummary {
  readonly lines: readonly { readonly product: ProductView; readonly count: number }[]
  /** Every product counted. */
  readonly items: number
  readonly subtotalCents: number
  readonly feeCents: number
  readonly totalCents: number
}

/** Every product the vendor sells, by item. */
export function productsOf(vendor: VendorView): ReadonlyMap<string, ProductView> {
  return new Map(
    vendor.sections.flatMap((section) => section.products.map((p) => [p.item, p] as const)),
  )
}

/**
 * Prices `counts` as the engine will (`quote`): lines in basket order, then the fee. An item
 * the vendor doesn't sell (any more) is left out; it wouldn't be sent.
 */
export function summarize(
  vendor: VendorView,
  products: ReadonlyMap<string, ProductView>,
  counts: ReadonlyMap<string, number>,
): BasketSummary {
  const lines = [...counts].flatMap(([item, count]) => {
    const product = products.get(item)
    return product ? [{ product, count }] : []
  })
  const items = lines.reduce((sum, line) => sum + line.count, 0)
  const subtotalCents = lines.reduce((sum, line) => sum + line.product.priceCents * line.count, 0)
  return {
    lines,
    items,
    subtotalCents,
    feeCents: vendor.feeCents,
    totalCents: subtotalCents + vendor.feeCents,
  }
}

/** The lines as `client.shops.order` takes them. */
export function orderLines(summary: BasketSummary): readonly BasketLine[] {
  return summary.lines.map((line) => ({ item: line.product.item, count: line.count }))
}
